import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import EducationalDisclaimer from '@/components/feature/EducationalDisclaimer';

const NevadaTrailerRegistrationPage: React.FC = () => (
  <div className="min-h-screen bg-white">
    <Header />
    <section className="pt-28 pb-12 bg-obsidian-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center space-x-2 text-sm text-obsidian-400 mb-4">
          <Link to="/" className="hover:text-gold-400">Home</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/nevada-dmv-info" className="hover:text-gold-400">DMV Info</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-gold-400">Trailer Registration</span>
        </div>
        <h1 className="font-display text-4xl font-bold text-white mb-3">Nevada Trailer Registration</h1>
        <p className="text-obsidian-300">Requirements and fees for registering trailers in Nevada — utility, boat, horse, and cargo trailers.</p>
      </div>
    </section>
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <EducationalDisclaimer />
      <div className="space-y-8 text-obsidian-700">
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Trailer Registration Overview</h2>
          <p>All trailers used on Nevada public roads must be registered and titled with the Nevada DMV. Trailers have some key differences from standard vehicle registration:</p>
          <div className="mt-4 bg-green-50 border border-green-200 rounded-xl p-5">
            <p className="font-semibold text-green-800 text-sm mb-2">Trailers Are Exempt From:</p>
            <ul className="space-y-1">
              {['Insurance requirements', 'Smog/emissions testing', 'Odometer disclosure requirements'].map(item => (
                <li key={item} className="flex items-center space-x-2 text-sm text-green-700"><i className="ri-checkbox-circle-line"></i><span>{item}</span></li>
              ))}
            </ul>
          </div>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Trailer Registration Fees</h2>
          <div className="bg-obsidian-50 rounded-xl p-6">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-obsidian-200"><th className="text-left py-2 font-semibold text-obsidian-900">Trailer Weight</th><th className="text-right py-2 font-semibold text-obsidian-900">Annual Fee</th></tr></thead>
              <tbody className="divide-y divide-obsidian-100">
                {[['1,000 lbs or less', '$12.00'], ['Over 1,000 lbs', '$24.00'], ['Title Fee', '$28.25'], ['3-Year Registration Option', 'Available — multiply annual fee by 3']].map(([l, a]) => (
                  <tr key={l}><td className="py-2.5 text-obsidian-700">{l}</td><td className="py-2.5 text-right font-medium text-obsidian-900">{a}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">What About Boats?</h2>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <p className="text-amber-800 text-sm"><strong>Important:</strong> Boats are NOT registered with the Nevada DMV. Boats are registered and titled by the <strong>Nevada Department of Wildlife (NDOW)</strong>. Boat registration fees range from $20 to $75+ depending on the length of the vessel.</p>
          </div>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Required Documents</h2>
          <ul className="space-y-3">
            {[
              'Completed Trailer Registration Application (Form VP 040)',
              'Proof of ownership (title or bill of sale)',
              'Payment for registration and title fees',
              'VIN or serial number of the trailer',
            ].map(item => (
              <li key={item} className="flex items-start space-x-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><i className="ri-checkbox-circle-line text-green-500"></i></div>
                <span className="text-sm">{item}</span>
              </li>
            ))}
          </ul>
        </section>
        <div className="bg-gold-50 border border-gold-200 rounded-xl p-6 text-center">
          <h3 className="font-display font-bold text-obsidian-900 text-lg mb-2">Need Help Registering Your Trailer?</h3>
          <p className="text-obsidian-600 text-sm mb-4">Speedy DMV Services handles trailer registration in Las Vegas. Fast and hassle-free.</p>
          <Link to="/appointment" className="inline-block bg-gold-gradient text-obsidian-900 px-6 py-2.5 rounded-lg font-bold text-sm hover:brightness-110 transition-all whitespace-nowrap">Book Appointment</Link>
        </div>
      </div>
    </div>
    <Footer />
  </div>
);

export default NevadaTrailerRegistrationPage;
